import { useState } from "react"

import { Button, SearchInput } from "web/components"
import Map from "./Map"

import { useGoogleMaps } from "web/hooks/data"
import { useModals } from "web/hooks/modals"

import { isAValue } from "web/services/helpers"

export function SelectLocation({
  setLocation,
  location,
  simpleSelection = false,
}) {
  const [suggestions, setSuggestions] = useState([])
  const { placeDetails, reverseGeocode } = useGoogleMaps()

  async function getReverseGeoCodeSuggestions(coordinates) {
    const results = await reverseGeocode(coordinates)
    const hasResults = Array.isArray(results) && results.length > 0

    const suggestions = hasResults
      ? results.filter((el) => isAValue(el.street_address)).slice(0, 3)
      : []

    const latLngSuggestion = {
      place_id: null,
      longitude: coordinates.longitude,
      latitude: coordinates.latitude,
    }

    if (hasResults) {
      latLngSuggestion["city"] = results[0].city
      latLngSuggestion["state"] = results[0].state
      latLngSuggestion["country"] = results[0].country
      latLngSuggestion["postal_code"] = results[0].postal_code
      latLngSuggestion["country_code"] = results[0].country_code
    }

    return [latLngSuggestion, ...suggestions]
  }

  async function handleSearchSelect(result) {
    const locationForResult = await placeDetails(result.place_id)

    setLocation(locationForResult)
    setSuggestions([locationForResult])
  }

  async function handleMapSelect(coordinates) {
    setLocation(coordinates)
    if (!simpleSelection) {
      const newSuggestions = await getReverseGeoCodeSuggestions(coordinates)
      setLocation(newSuggestions[0])
      setSuggestions(newSuggestions)
    }
  }

  return (
    <>
      {simpleSelection ? (
        <Map
          location={location}
          onSelect={handleMapSelect}
          zoomButtons={true}
        />
      ) : (
        <div
          className="grid h-full gap-y-2.5"
          style={{ gridTemplateRows: "auto 1fr" }}
        >
          <SearchInput
            scope="place"
            onSelect={handleSearchSelect}
            inputTestId={"location-search"}
          />
          <LocationSuggestions
            suggestions={suggestions}
            location={location}
            onClick={setLocation}
          />
          <Map location={location} onSelect={handleMapSelect} />
        </div>
      )}
    </>
  )
}

function SelectLocationModal({ respond }) {
  const [location, setLocation] = useState()

  async function handleConfirmSelection() {
    respond(location)
  }

  return (
    <>
      <div className="modal-content-above-commands">
        <SelectLocation setLocation={setLocation} location={location} />
      </div>
      <div className="modal-commands">
        <div className="right-buttons">
          <Button onClick={handleConfirmSelection}>Select</Button>
          <Button variant="tertiary_default" onClick={respond}>
            Cancel
          </Button>
        </div>
      </div>
    </>
  )
}

function LocationSuggestions({ suggestions, location, onClick }) {
  return (
    <div className="absolute right-10 top-20 z-10">
      {suggestions.map((suggestion, index) => (
        <label
          key={suggestion.place_id || index}
          className="mb-2.5 flex cursor-pointer items-center rounded-md bg-white p-2.5 shadow"
        >
          <input
            type="checkbox"
            checked={suggestion.place_id === location?.place_id}
            onChange={() => onClick(suggestion)}
            className="mr-1.5 size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400/50"
            style={{ width: "16px", height: "16px" }}
          />
          {suggestion.street_address || "Dropped Pin"}
        </label>
      ))}
    </div>
  )
}

export default function useSelectLocationModal() {
  const { showModal } = useModals()

  return async function () {
    return await showModal({
      title: "Select Location",
      fullScreen: false,
      heightPixels: 600,
      widthPixels: 800,
      component: ({ respond }) => <SelectLocationModal respond={respond} />,
    })
  }
}
